// c:\Users\Seshu\Downloads\Genmech\Genmech\client\src\context\SyncContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { db } from "../utils/offlineDb";
import { checkRealOnlineStatus } from "../utils/offlineAdapter";
import { API_BASE_URL } from "../config/api";

const SyncContext = createContext(null);

// Module-level lock to strictly prevent concurrent synchronizations
let isSyncInProgress = false;
let activeSyncPromise = null;
const CACHE_FRESHNESS_TTL_MS = 30 * 60 * 1000; // 30 minutes cache freshness

export const useSync = () => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error("useSync must be used within a SyncProvider");
  }
  return context;
};

export const SyncProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(localStorage.getItem("genmech:last_sync_time") || null);
  const [syncFailed, setSyncFailed] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Monitor queue count
  const updateQueueCount = async () => {
    try {
      const count = await db.syncQueue.count();
      setPendingCount(count);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    updateQueueCount();
    const interval = setInterval(updateQueueCount, 3000);
    return () => clearInterval(interval);
  }, []);

  // Monitor network status, visibility changes, and initial mount sync
  useEffect(() => {
    let isMounted = true;

    const handleOnlineStatusChange = async () => {
      const online = await checkRealOnlineStatus();
      if (isMounted) setIsOnline(online);
      if (online) {
        // Automatically sync when connection returns
        triggerSync();
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        const online = await checkRealOnlineStatus();
        if (isMounted) setIsOnline(online);
        if (online) {
          triggerSync();
        }
      }
    };

    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initial mount sync check
    checkRealOnlineStatus().then((online) => {
      if (isMounted) setIsOnline(online);
      if (online) {
        triggerSync();
      }
    });

    // Periodic heartbeat check
    const checkInterval = setInterval(async () => {
      const online = await checkRealOnlineStatus();
      if (isMounted) setIsOnline(online);
      if (online) {
        triggerSync();
      }
    }, 30000);

    return () => {
      isMounted = false;
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(checkInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Performs resolution of client-generated IDs within a JSON payload
   */
  const resolvePayloadIds = (data, idMap) => {
    if (!data) return data;
    let json = typeof data === "string" ? data : JSON.stringify(data);
    for (const [localId, serverId] of Object.entries(idMap)) {
      json = json.split(localId).join(serverId);
    }
    return JSON.parse(json);
  };

  /**
   * Fetches latest data snapshots from server to keep IndexedDB fresh
   */
  const refreshLocalCache = async () => {
    const endpoints = [
      { url: "/api/customers", table: db.customers },
      { url: "/api/dealers", table: db.dealers },
      { url: "/api/customer-groups", table: db.customerGroups },
      { url: "/api/material-stock/inventory", table: db.inventories },
      { url: "/api/material-stock/storage-locations", table: db.storageLocations },
      { url: "/api/material-stock/storage-areas", table: db.storageAreas },
      { url: "/api/material-stock", table: db.materialStocks },
      { url: "/api/vouchers", table: db.vouchers },
      { url: "/api/vouchers/allowances", table: db.vouchers },
      { url: "/api/travel", table: db.travels },
      { url: "/api/complaints", table: db.complaints },
      { url: "/api/visits", table: db.visits },
      { url: "/api/project-expenses", table: db.projectExpenses },
      { url: "/api/quotations", table: db.quotations },
      { url: "/api/delivery-challans", table: db.deliveryChallans },
      { url: "/api/earth-pits", table: db.earthPits },
      { url: "/api/general-services", table: db.generalServices },
      { url: "/api/daily-updates", table: db.dailyUpdates },
      { url: "/api/accounting-notes", table: db.accountingNotes }
    ];

    for (const endpoint of endpoints) {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Fetch bypasses the custom adapter by using standard fetch
        const res = await fetch(`${API_BASE_URL}${endpoint.url}`, { headers }).catch(() => null);
        if (res && res.ok) {
          const serverData = await res.json().catch(() => null);
          if (Array.isArray(serverData)) {
            if (endpoint.table === db.vouchers) {
              if (endpoint.url === "/api/vouchers") {
                const keysToDelete = await db.vouchers
                  .filter((r) => r.expenseType !== undefined || r.voucherDate !== undefined)
                  .primaryKeys();
                await db.vouchers.bulkDelete(keysToDelete);
              } else if (endpoint.url === "/api/vouchers/allowances") {
                const keysToDelete = await db.vouchers
                  .filter((r) => r.givenByRole !== undefined || r.allowanceDate !== undefined)
                  .primaryKeys();
                await db.vouchers.bulkDelete(keysToDelete);
              }
            } else {
              await endpoint.table.clear();
            }
            await endpoint.table.bulkPut(
              serverData.map((item) => ({ ...item, syncStatus: "synced", lastSyncedAt: new Date().toISOString() }))
            );
          }
        }
      } catch (err) {
        console.warn(`Failed to refresh cache for ${endpoint.url}:`, err);
      }
    }
  };

  const triggerSync = async (options = {}) => {
    const force = options?.force === true;

    // Prevent concurrent sync executions synchronously
    if (isSyncInProgress && activeSyncPromise) {
      return activeSyncPromise;
    }
    
    const token = localStorage.getItem("token");
    if (!token) {
      console.log("[Sync] User not logged in. Postponing synchronization.");
      return;
    }

    activeSyncPromise = (async () => {
      isSyncInProgress = true;
      setIsSyncing(true);

      try {
        const queue = await db.syncQueue.orderBy("timestamp").toArray();
        if (queue.length === 0) {
          setSyncFailed(false);
          setSessionExpired(false);

          // Check if the local cache is already fresh enough to skip downloading 19 collections
          const lastRefreshTs = Number(localStorage.getItem("genmech:last_cache_refresh_ts")) || 0;
          const isCacheFresh = !force && lastRefreshTs > 0 && Date.now() - lastRefreshTs < CACHE_FRESHNESS_TTL_MS;

          if (isCacheFresh) {
            return;
          }

          try {
            await refreshLocalCache();
            const now = Date.now();
            const syncTime = new Date(now).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
            localStorage.setItem("genmech:last_sync_time", syncTime);
            localStorage.setItem("genmech:last_cache_refresh_ts", String(now));
            setLastSyncTime(syncTime);
          } catch (err) {
            console.warn("[Sync Engine] Failed to refresh local cache:", err);
          }
          return;
        }

        console.log(`[Sync Engine] Starting sync. Processing ${queue.length} pending items.`);
        setSyncFailed(false);
        setSessionExpired(false);

        const idMap = {}; // Maps localIds -> server ObjectIDs

        for (const item of queue) {
          const originalPayload = JSON.parse(item.data || "{}");
          // Resolve references to any client-side generated UUIDs
          const resolvedPayload = resolvePayloadIds(originalPayload, idMap);

          // Strip local helper flags before sending to the backend
          if (resolvedPayload) {
            delete resolvedPayload.syncStatus;
            delete resolvedPayload.isDeleted;
          }

          let response;
          try {
            const method = String(item.method || "get").toUpperCase();
            const hasBody = !["GET", "HEAD"].includes(method);
            let fetchUrl = item.url;
            if (fetchUrl && fetchUrl.startsWith("/")) {
              fetchUrl = `${API_BASE_URL}${fetchUrl}`;
            }

            const fetchConfig = {
              method,
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
              },
              body: hasBody && resolvedPayload ? JSON.stringify(resolvedPayload) : undefined,
              cache: "no-store"
            };

            const res = await fetch(fetchUrl, fetchConfig);
            if (!res.ok) {
              const errData = await res.json().catch(() => ({}));
              const error = new Error(`Request failed with status code ${res.status}`);
              error.response = { status: res.status, data: errData };
              throw error;
            }
            const data = res.status === 204 ? null : await res.json().catch(() => null);
            response = { data };
          } catch (xhrError) {
            if (xhrError.response?.status === 401) {
              setSessionExpired(true);
              setSyncFailed(true);
              console.error("[Sync Engine] Session expired (401). Pausing queue.");
              return;
            }
            throw xhrError; // Let it bubble to main try/catch to stop the sync loop
          }

          const serverRecord = response.data;
          const localTable = db[item.url.split("/").pop().replace(/-([a-z])/g, (g) => g[1].toUpperCase()) + "s"] || db.materialStocks;

          if (item.method === "POST" && serverRecord && serverRecord._id) {
            const localId = item.localId;
            const serverId = serverRecord._id;

            if (localId && serverId && localId !== serverId) {
              idMap[localId] = serverId;
              // Clear local entry and save the server-confirmed record
              await localTable.delete(localId);
            }
            await localTable.put({ ...serverRecord, syncStatus: "synced", lastSyncedAt: new Date().toISOString() });
          } else if (item.method === "PUT" && serverRecord && serverRecord._id) {
            await localTable.put({ ...serverRecord, syncStatus: "synced", lastSyncedAt: new Date().toISOString() });
          } else if (item.method === "DELETE" && item.localId) {
            await localTable.delete(item.localId);
          }

          // Remove successfully synchronised task from the queue
          await db.syncQueue.delete(item.id);
          await db.syncLogs.add({
            timestamp: new Date().toISOString(),
            action: `${item.method} ${item.url}`,
            status: "success"
          });
        }

        const now = Date.now();
        const syncTime = new Date(now).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        localStorage.setItem("genmech:last_sync_time", syncTime);
        localStorage.setItem("genmech:last_cache_refresh_ts", String(now));
        setLastSyncTime(syncTime);
        setSyncFailed(false);
        // Pull fresh data snapshots
        await refreshLocalCache();
      } catch (syncError) {
        console.error("[Sync Engine Error] Queue execution paused:", syncError);
        setSyncFailed(true);
        await db.syncLogs.add({
          timestamp: new Date().toISOString(),
          action: "Queue Execution",
          status: "failed",
          error: syncError.message || "Unknown Network Error"
        });
      } finally {
        isSyncInProgress = false;
        activeSyncPromise = null;
        setIsSyncing(false);
        updateQueueCount();
      }
    })();

    return activeSyncPromise;
  };

  const handleSyncNow = () => triggerSync({ force: true });

  return (
    <SyncContext.Provider
      value={{
        isOnline,
        isSyncing,
        pendingCount,
        lastSyncTime,
        syncFailed,
        sessionExpired,
        syncNow: handleSyncNow
      }}
    >
      {children}
    </SyncContext.Provider>
  );
};

// UI Widget Component for layouts
export const SyncStatusWidget = () => {
  const { isOnline, isSyncing, pendingCount, lastSyncTime, syncFailed, sessionExpired, syncNow } = useSync();

  let statusColor = "bg-green-500";
  let statusText = "Online | Synced";

  if (isSyncing) {
    statusColor = "bg-blue-500 animate-pulse";
    statusText = "Syncing...";
  } else if (!isOnline) {
    statusColor = "bg-gray-500";
    statusText = `Offline | ${pendingCount} Pending`;
  } else if (sessionExpired) {
    statusColor = "bg-red-500";
    statusText = "Session Expired";
  } else if (syncFailed) {
    statusColor = "bg-red-500";
    statusText = "Sync Failed";
  } else if (pendingCount > 0) {
    statusColor = "bg-yellow-500 animate-pulse";
    statusText = `${pendingCount} Changes Pending`;
  }

  return (
    <div className="flex items-center gap-3 bg-white/70 dark:bg-slate-900/70 backdrop-blur-md px-4 py-2.5 rounded-full border border-slate-200 dark:border-slate-800 shadow-sm text-xs font-semibold select-none">
      {/* Network Dot */}
      <span className={`relative flex h-2 w-2 rounded-full ${statusColor}`}>
        {isSyncing || (isOnline && pendingCount > 0) ? (
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-inherit opacity-75"></span>
        ) : null}
      </span>

      {/* Text Info */}
      <div className="flex flex-col">
        <span className="text-[10px] text-slate-400 dark:text-slate-500 leading-none">SYSTEM SYNC</span>
        <span className="text-slate-700 dark:text-slate-200 mt-0.5">{statusText}</span>
      </div>

      {/* Sync Button */}
      {isOnline && (pendingCount > 0 || syncFailed || sessionExpired) ? (
        <button
          onClick={syncNow}
          disabled={isSyncing}
          className="ml-2 flex items-center justify-center p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors group"
          title="Sync Now"
        >
          <span
            className={`material-symbols-outlined text-sm text-primary group-hover:rotate-180 transition-transform duration-500 ${
              isSyncing ? "animate-spin" : ""
            }`}
          >
            sync
          </span>
        </button>
      ) : null}

      {/* Last Sync Info */}
      {lastSyncTime && !isSyncing && pendingCount === 0 && (
        <span className="text-[10px] text-slate-400 dark:text-slate-500 border-l border-slate-200 dark:border-slate-800 pl-3">
          Last: {lastSyncTime}
        </span>
      )}
    </div>
  );
};
